import React, { useMemo } from 'react';

import PresenceAvatarGroup from '@sharedCR/common/PresenceAvatarGroup';
import StatusIcon from '@sharedCR/common/StatusIcon';
import MilestoneStatusCellRenderer from 'app/pages/loanPipelines/LoanPipelinesTable/common/LoanPipelinesCellRenderer/MilestoneStatusCellRenderer';
import Tag from 'app/shared/components/common/Tag';
import PropTypes from 'prop-types';

import { WidgetType } from 'constants/loanApplication';
import { StatusEnum } from 'constants/status';
import { TagType } from 'constants/tag';

import { ausBooleanFields } from '../LoanHeaders.constants';
import MilestoneRoadmapWidget from '../MilestoneRoadmapWidget';

import {
  AUS_NA_FALLBACK_TEXT,
  AUS_NA_TEXT,
  AUS_STATUS_ICON_SIZE,
  AUS_TYPE_FIELD_NAME,
  STATUS_FIELD_NAME,
  TAG_WIDGET_CONFIGS,
} from './HeaderItemValue.constants';

const HeaderItemValue = ({
  dynamicFieldId = null,
  normalizedName = '',
  formattedValue = '',
  rawValue = null,
  assignedUsers = null,
  loanNumber = null,
  milestoneStatusName = null,
  ausBooleanStatusOverride = null,
  renderText,
}) => {
  const statusFieldValue = useMemo(() => ({ name: rawValue }), [rawValue]);

  if (dynamicFieldId === WidgetType.LOAN_ROLES.id) {
    return assignedUsers?.length ? <PresenceAvatarGroup usersData={assignedUsers} /> : null;
  }

  if (dynamicFieldId === WidgetType.MILESTONE_STATUS.id) {
    return <MilestoneRoadmapWidget milestoneStatusName={milestoneStatusName} />;
  }

  if (dynamicFieldId === WidgetType.LOAN_NUMBER.id) {
    return renderText(loanNumber ? `#${loanNumber}` : '', loanNumber ?? '');
  }

  const tagWidgetConfig = TAG_WIDGET_CONFIGS[dynamicFieldId];

  if (tagWidgetConfig) {
    if (!rawValue) {
      return null;
    }

    return (
      <Tag
        backgroundColor={tagWidgetConfig.backgroundColor}
        color={tagWidgetConfig.color}
        text={tagWidgetConfig.text}
        type={TagType.SMALL_FAT}
      />
    );
  }

  if (normalizedName === STATUS_FIELD_NAME) {
    return (
      <MilestoneStatusCellRenderer isNameKey type={TagType.SMALL_FAT} value={statusFieldValue} />
    );
  }

  if (normalizedName === AUS_TYPE_FIELD_NAME) {
    return formattedValue === AUS_NA_TEXT
      ? renderText(AUS_NA_FALLBACK_TEXT)
      : renderText(formattedValue);
  }

  if (ausBooleanFields.includes(normalizedName)) {
    if (formattedValue === AUS_NA_TEXT) {
      return renderText(AUS_NA_FALLBACK_TEXT);
    }

    const status =
      ausBooleanStatusOverride ?? (formattedValue ? StatusEnum.SUCCESS : StatusEnum.ERROR);

    return <StatusIcon size={AUS_STATUS_ICON_SIZE} status={status} />;
  }

  return renderText(formattedValue);
};

HeaderItemValue.propTypes = {
  renderText: PropTypes.func.isRequired,
  dynamicFieldId: PropTypes.string,
  normalizedName: PropTypes.string,
  formattedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  rawValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  assignedUsers: PropTypes.array,
  loanNumber: PropTypes.string,
  milestoneStatusName: PropTypes.string,
  ausBooleanStatusOverride: PropTypes.string,
};

export default HeaderItemValue;
